import { Download, Pause, Play } from "lucide-react";
import { PageLayout, Section } from "../components/PageLayout";
import { WideOpener, AtmoBreak, StoryTriptych, SideBySide } from "../components/ImageBlocks";
import { useLanguage } from "../contexts/LanguageContext";
import useSharedAudio from "../hooks/useSharedAudio";
import { forceDownload } from "../utils/download";

const AUDIO_SIDE_A = `${process.env.PUBLIC_URL}/audio/side-a.mp3`;
const AUDIO_SIDE_B = `${process.env.PUBLIC_URL}/audio/side-b.mp3`;
const AUDIO_SIDE_PL = `${process.env.PUBLIC_URL}/audio/side-pl.mp3`;
const PDF_DE = `${process.env.PUBLIC_URL}/downloads/songtext-de.pdf`;
const PDF_EN = `${process.env.PUBLIC_URL}/downloads/lyrics-en.pdf`;
const PDF_PL = `${process.env.PUBLIC_URL}/downloads/lyrics-pl.pdf`;

export default function DerSong() {
  const { t } = useLanguage();
  const audio = useSharedAudio(AUDIO_SIDE_A, false);

  return (
    <PageLayout>
      <WideOpener
        src="wide-2-0_tiefe_21-9_v01.jpeg"
        alt={t("menuSong")}
        heading={t("menuSong")}
      />

      <div
        className="mx-auto w-full max-w-3xl text-[17px] leading-[1.6] sm:text-[18px]"
        style={{ color: "#1A1A1A" }}
        data-testid="page-song-intro"
      >
        <SideBySide src="IST_horizont_q_v01.jpg" alt="">
          <p>{t("song_intro_1")}</p>
          <p>{t("song_intro_2")}</p>
          <p>{t("song_intro_3")}</p>
        </SideBySide>
      </div>

      <Section heading={t("song_21_heading")} testId="section-anhoeren">
        <SideBySide src="IST_licht_q_v01.jpg" alt="">
          <p>{t("song_21_p1")}</p>
          <div
            data-testid="song-inline-player"
            className="flex items-center gap-4 rounded border p-5"
            style={{
              borderColor: "rgba(42,38,36,0.18)",
              backgroundColor: "rgba(42,38,36,0.03)",
            }}
          >
            <button
              type="button"
              onClick={audio.toggle}
              disabled={audio.error}
              data-testid="song-inline-play-button"
              aria-label={audio.isPlaying ? t("pause") : t("play")}
              className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full text-white transition-all duration-200 hover:scale-105 active:scale-95 disabled:opacity-40"
              style={{ backgroundColor: "#2A2624" }}
            >
              {audio.isPlaying ? (
                <Pause className="h-6 w-6 fill-current" />
              ) : (
                <Play className="ml-0.5 h-6 w-6 fill-current" />
              )}
            </button>
            <div className="min-w-0 flex-1">
              <p className="text-[16px]" style={{ color: "#1A1A1A", fontWeight: 600 }}>
                {t("song_21_player_title")}
              </p>
            </div>
          </div>
          <p>{t("song_21_p2")}</p>
        </SideBySide>
      </Section>

      <WideOpener
        src="wide-2-2_akzent-tiefe_21-9_v01.jpeg"
        alt={t("song_22_heading")}
        heading={t("song_22_heading")}
      />

      <Section testId="section-bedeutung">
        <SideBySide src="IST_impuls_q_v01.jpg" alt="">
          <p>{t("song_22_p1")}</p>
          <p>{t("song_22_p2")}</p>
          <p>{t("song_22_p3")}</p>
        </SideBySide>
        <AtmoBreak src="2-1_impuls_16_v01.jpg" alt="" />
      </Section>

      <WideOpener
        src="wide-2-2_akzent-tiefe_21-9_v01.jpeg"
        alt={t("song_23_heading")}
        heading={t("song_23_heading")}
      />

      <Section testId="section-text-story">
        <p>{t("song_23_p1")}</p>
        <p>{t("song_23_p2")}</p>

        <StoryTriptych
          images={[
            "2-1_impuls1_q_v01.jpg",
            "2-2_resonanz1_q_v01_new.jpg",
            "2-3_story1_q_v01_new.jpg",
          ]}
        />

        <p>{t("song_23_p3")}</p>
        <p>{t("song_23_p4")}</p>
      </Section>

      <WideOpener
        src="wide-2-4_ruhepunkt_21-9_v01.jpeg"
        alt={t("song_24_heading")}
        heading={t("song_24_heading")}
      />

      <Section testId="section-download">
        <SideBySide src="IST_visionen_q_v01.jpg" alt="">
          <p>{t("song_24_p1")}</p>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => forceDownload(AUDIO_SIDE_A, "raus-aus-dem-falschen-leben.mp3")}
              data-testid="download-song-de"
              className="inline-flex items-center justify-between gap-3 text-base text-white transition-colors duration-200 hover:opacity-90"
              style={{
                backgroundColor: "#2A2624",
                padding: "14px 22px",
                borderRadius: "5px",
                fontWeight: 500,
                letterSpacing: "0.3px",
              }}
            >
              <span>{t("song_24_btn_song_de")}</span>
              <Download className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => forceDownload(PDF_DE, "RAUS-AUS-DEM-falschen-LEBEN-Der-SONGTEXT.pdf")}
              data-testid="download-lyrics-de"
              className="inline-flex items-center justify-between gap-3 text-base text-white transition-colors duration-200 hover:opacity-90"
              style={{
                backgroundColor: "#2A2624",
                padding: "14px 22px",
                borderRadius: "5px",
                fontWeight: 500,
                letterSpacing: "0.3px",
              }}
            >
              <span>{t("song_24_btn_lyrics_de")}</span>
              <Download className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => forceDownload(AUDIO_SIDE_B, "out-of-the-wrong-life.mp3")}
              data-testid="download-song-en"
              className="inline-flex items-center justify-between gap-3 text-base text-white transition-colors duration-200 hover:opacity-90"
              style={{
                backgroundColor: "#2A2624",
                padding: "14px 22px",
                borderRadius: "5px",
                fontWeight: 500,
                letterSpacing: "0.3px",
              }}
            >
              <span>{t("song_24_btn_song_en")}</span>
              <Download className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => forceDownload(PDF_EN, "OUT-OF-THE-wrong-LIFE-The-SONG-LYRICS.pdf")}
              data-testid="download-lyrics-en"
              className="inline-flex items-center justify-between gap-3 text-base text-white transition-colors duration-200 hover:opacity-90"
              style={{
                backgroundColor: "#2A2624",
                padding: "14px 22px",
                borderRadius: "5px",
                fontWeight: 500,
                letterSpacing: "0.3px",
              }}
            >
              <span>{t("song_24_btn_lyrics_en")}</span>
              <Download className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => forceDownload(AUDIO_SIDE_PL, "wyjdz-z-falszywego-zycia.mp3")}
              data-testid="download-song-pl"
              className="inline-flex items-center justify-between gap-3 text-base text-white transition-colors duration-200 hover:opacity-90"
              style={{
                backgroundColor: "#2A2624",
                padding: "14px 22px",
                borderRadius: "5px",
                fontWeight: 500,
                letterSpacing: "0.3px",
              }}
            >
              <span>Pobierz Polską wersję (MP3)</span>
              <Download className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => forceDownload(PDF_PL, "WYJDZ-Z-falszywego-ZYCIA-tekst.pdf")}
              data-testid="download-lyrics-pl"
              className="inline-flex items-center justify-between gap-3 text-base text-white transition-colors duration-200 hover:opacity-90"
              style={{
                backgroundColor: "#2A2624",
                padding: "14px 22px",
                borderRadius: "5px",
                fontWeight: 500,
                letterSpacing: "0.3px",
              }}
            >
              <span>Tekst Polski (PDF)</span>
              <Download className="h-5 w-5" />
            </button>
          </div>

          <p className="border-l-2 pl-5 italic" style={{ borderColor: "#2A2624" }}>
            {t("song_24_quote")}
          </p>
        </SideBySide>
      </Section>
    </PageLayout>
  );
}
